import { useState } from "react";
import type { MarketplaceEntry } from "../types/index";
import { env } from "../lib/env";
import { isAuthenticated } from "../lib/auth";
import { getPresignedDownloadUrl, legacyInstallKey } from "../lib/github-proxy-client";
import ChevronIcon from "../icons/ChevronIcon";

interface Props {
  plugin: MarketplaceEntry;
}

function DownloadIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default function DesktopDownload({ plugin }: Props) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  // Downloading needs a signed-in session (presigned S3 URL via the proxy), so
  // there's nothing to show signed-out — hide the whole block rather than prompt.
  if (!env.REACT_APP_GITHUB_PROXY_URL || !isAuthenticated()) {
    return null;
  }

  // For github-proxy calls we always use the legacy install-key shape
  // (source--org--repo[--skill-path]) — it's what parseInstallKey decodes.
  const proxyKey = legacyInstallKey(plugin);

  // Synthesized skill entries from multi-skill plugins have installName in the form
  // "<parent-manifestName>--skills--<slug>" and NO skillPath field. Detect by
  // scanning installName for the marker. Standalone skill-path entries DO have
  // skillPath set, so we never decompose them here.
  const skillsMarker = "--skills--";
  const installNameOrEmpty = plugin.installName ?? "";
  const skillsIdx = !plugin.skillPath ? installNameOrEmpty.lastIndexOf(skillsMarker) : -1;
  const isSynthesized = skillsIdx !== -1;
  const synthesizedSkillName = isSynthesized
    ? installNameOrEmpty.slice(skillsIdx + skillsMarker.length)
    : undefined;

  async function handleDownload() {
    setDownloading(true);
    setError("");
    if (isSynthesized && !synthesizedSkillName) {
      setError("This skill entry has a malformed install key — please report this to the marketplace team.");
      setDownloading(false);
      return;
    }
    try {
      const url = await getPresignedDownloadUrl(
        proxyKey,
        isSynthesized ? undefined : plugin.skillPath,
        synthesizedSkillName,
      );
      // A 200 with no URL would otherwise navigate to "undefined" silently.
      if (!url) {
        setError("The server did not return a download link — please try again or report this.");
        return;
      }
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  const isPlugin = plugin.extensionType === "plugin";
  const fileLabel = isPlugin ? ".plugin file" : "skill.md file";

  return (
    <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid #eee" }}>
      <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "0.5rem" }}>
        Or download the file
      </div>

      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          width: "100%",
          padding: "0.5rem 1rem",
          fontSize: "0.85rem",
          fontWeight: 600,
          backgroundColor: "#fff",
          color: "#0078d4",
          border: "1px solid #0078d4",
          borderRadius: "6px",
          cursor: downloading ? "wait" : "pointer",
          boxSizing: "border-box",
          opacity: downloading ? 0.7 : 1,
        }}
      >
        <DownloadIcon />
        {downloading ? "Preparing download…" : "Download"}
      </button>

      {error && (
        <div style={{ marginTop: "0.4rem", fontSize: "0.8rem", color: "#cc0000" }}>
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowInstructions(!showInstructions)}
        aria-expanded={showInstructions}
        aria-controls="download-install-steps"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.35rem",
          marginTop: "0.6rem",
          padding: 0,
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "0.8rem",
          color: "#0078d4",
        }}
      >
        How to add the file
        <ChevronIcon expanded={showInstructions} />
      </button>

      {/* Always rendered (toggled with `hidden`) so the button's aria-controls
          reference stays valid even when collapsed. */}
      <div id="download-install-steps" hidden={!showInstructions} style={{ marginTop: "0.5rem", fontSize: "0.8rem", lineHeight: "1.6", color: "#444" }}>
        <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li>Click <strong>Download</strong> above to save the {fileLabel}</li>
            {isPlugin ? (
              <li>In Claude, go to <strong>Customize → Upload Plugin</strong></li>
            ) : (
              <li>In Claude, go to <strong>Customize → Skills → Create skill → Upload a skill</strong></li>
            )}
            <li>Select the downloaded {fileLabel}</li>
            <li>It&rsquo;s available in your next conversation{isPlugin ? " (hooks and sub-agents require Cowork)" : ""}</li>
          </ol>
      </div>
    </div>
  );
}
